import { useSearchPlayersMutation, useWhisperMutation } from '@http';
import { TextInput } from '@components/ui/TextInput';
import { Button } from '@components/ui/Button';
import { useTranslation } from 'react-i18next';
import { Modal } from '@components/ui/Modal';
import { useEffect, useState } from 'react';
import { useChatStore } from '@stores';

interface WhisperPlayerModalProps {
  children: React.ReactNode;
  className?: string;
};

export function WhisperPlayerModal({ children, className }: WhisperPlayerModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [players, setPlayers] = useState<{ username: string; id: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation(undefined, { keyPrefix: 'Chat.WhisperPlayer' });
  const { mutateAsync: searchAsync } = useSearchPlayersMutation();
  const { mutateAsync: whisperAsync } = useWhisperMutation();
  const { addChannels, setActiveChannelId } = useChatStore();

  useEffect(() => {
    setPlayers([]);
    setIsLoading(true);

    const timeout = setTimeout(() => {
      if (!search.length) return setIsLoading(false);

      searchAsync({ name: search }).then((data) => {
        setPlayers(data ?? []);
        setIsLoading(false);
      });
    }, 500);

    return () => clearTimeout(timeout);
  }, [search]);

  const whisperPlayer = async (playerId: string) => {
    const data = await whisperAsync({ id: playerId });
    if (data && data.id) {
      addChannels([data]);
      setActiveChannelId(data.id);
    }
    close();
  };

  const close = () => {
    setPlayers([]);
    setSearch('');
    setIsOpen(false);
  };

  return (
    <>
      <span onClick={() => setIsOpen(true)} className={className}>
        {children}
      </span>
      <Modal onRequestClose={close} title={t('title')} isOpen={isOpen}>
        <TextInput placeholder={t('search')} onChange={setSearch} className="w-full" value={search} />

        {isLoading && (
          <div className="flex justify-center py-12">
            <span className="loader" />
          </div>
        )}

        {!isLoading && !players.length && (
          <div className="flex justify-center py-12">
            <p className="text-sm">{t(search ? 'noResults' : 'pleaseEnter')}</p>
          </div>
        )}

        {players.map((player, i) => (
          <div
            className={`${i < players.length - 1 && 'border-b border-secondary'} flex items-center justify-between p-2`}
            key={player.id}
          >
            <p>{player.username}</p>
            <Button onClick={() => whisperPlayer(player.id)} className="w-auto py-1">
              {t('whisper')}
            </Button>
          </div>
        ))}
      </Modal>
    </>
  );
}